import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  ReactiveFormsModule,
} from '@angular/forms';
import { SharedService } from 'src/app/shared/services/shared.service';
import { ExtensionService } from 'src/app/shared/services/extension.service';
import { Instructions } from '../constants';
import { Instruction } from '../models';
import { Subscription } from 'rxjs';
import {
  datePartitioner,
  findDriverLicense,
  findDriversLicenseByUserInfo,
  findStateByAbbreviation,
  getDay,
  getIdTypeLabel,
  getMonth,
  getStateNameByStateCode,
  getYear,
} from 'src/app/shared/functions';
import {
  ModalID,
  DateMonthPickerConfig,
  StatusModalIndicator,
  StateList,
  signatureType,
} from 'src/app/shared/models/constants';
import { BoundBookEntry } from 'src/app/shared/models/general';
import {
  NicsECheckExtensionMessageEnvelope,
  SendFormDataToExtensionMessage,
} from 'src/app/shared/models/nics-extension-message';
import {
  BsDatepickerConfig,
  BsDatepickerDirective,
  BsDatepickerModule,
} from 'ngx-bootstrap/datepicker';
import { NgSelectModule } from '@ng-select/ng-select';
import { checkForValidExpirationDate } from 'src/app/shared/validators/custom-validators';
import { DriverLicense } from 'src/app/shared/models/drivers-license-parser';

@Component({
  selector: 'app-seller-4473',
  templateUrl: './seller-4473.component.html',
  styleUrls: ['./seller-4473.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, BsDatepickerModule],
})
export class Seller4473Component implements OnInit {
  @ViewChild('idExpirationMonth', { static: false })
  idExpirationMonth: BsDatepickerDirective;
  @ViewChild('idExpirationMonth', { read: ElementRef })
  idExpirationMonthInput: ElementRef;
  @ViewChild('pcsExpirationMonth', { static: false })
  pcsExpirationMonth: BsDatepickerDirective;
  @ViewChild('pcsExpirationMonth', { read: ElementRef })
  pcsExpirationMonthInput: ElementRef;
  @ViewChild('pcs_month') pcsMonth: BsDatepickerDirective;
  @ViewChild('nicsTransmissionMonth')
  nicsTransmissionMonth: BsDatepickerDirective;
  @ViewChild('nicsTransmissionMonth', { read: ElementRef })
  nicsTransmissionMonthInput: ElementRef;
  @ViewChild('noNicsValidPermitIssuanceMonth')
  noNicsValidPermitIssuanceMonth: BsDatepickerDirective;
  @ViewChild('noNicsValidPermitIssuanceMonth', { read: ElementRef })
  noNicsValidPermitIssuanceMonthInput: ElementRef;
  @ViewChild('noNicsValidPermitExpirationMonth')
  noNicsValidPermitExpirationMonth: BsDatepickerDirective;
  @ViewChild('noNicsValidPermitExpirationMonth', { read: ElementRef })
  noNicsValidPermitExpirationMonthInput: ElementRef;

  private destroyRef = inject(DestroyRef);
  seller_4473_form: FormGroup;
  subscriptions = new Subscription();
  selected_instruction: Instruction = new Instruction();
  zip_codes: string[] = ['83646', '83645'];
  cities: string[] = ['Boise', 'Meridian'];
  states = StateList;
  minDate: Date = new Date(); // Add this line
  disable_datepicker: boolean = true;
  extensionInstalled = false;

  bsConfig: Partial<BsDatepickerConfig>;
  expBsConfig: Partial<BsDatepickerConfig>;

  constructor(
    protected element: ElementRef,
    private fb: FormBuilder,
    public sharedService: SharedService,
    private extensionService: ExtensionService,
  ) {
    // Initializing Form
    this.seller_4473_form = this.fb.group(
      {
        transfer_handgun: new FormControl({ value: false, disabled: false }),
        transfer_long_gun: new FormControl({ value: false, disabled: false }),
        transfer_other_firearm: new FormControl({
          value: false,
          disabled: false,
        }),

        function_name: new FormControl({ value: null, disabled: false }),
        function_address: new FormControl({ value: null, disabled: false }),
        function_zip_code: new FormControl({ value: null, disabled: false }),
        function_city: new FormControl({ value: null, disabled: false }),
        function_state: new FormControl({ value: null, disabled: false }),
        function_county: new FormControl({ value: null, disabled: false }),

        id_issuing_authority: new FormControl(
          { value: null, disabled: false },
          [Validators.required],
        ),
        id_type_of_id: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        id_number: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        id_expiration_month: new FormControl({ value: null, disabled: false }, [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(2),
          Validators.pattern('^(0[1-9]|1[0-2])$'), // Ensure valid month (01-12)
          checkForValidExpirationDate('id_expiration'),
        ]),
        id_expiration_day: new FormControl({ value: null, disabled: false }, [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(2),
          Validators.pattern('^(0[1-9]|[12][0-9]|3[01])$'), // Ensure valid day (01-31)
          checkForValidExpirationDate('id_expiration'),
        ]),
        id_expiration_year: new FormControl({ value: null, disabled: false }, [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(4),
          Validators.pattern('^[0-9]{4}$'), // Ensure valid year
          checkForValidExpirationDate('id_expiration'),
        ]),
        supplemental_id: new FormControl({ value: null, disabled: false }),

        pcs_base_name: new FormControl({ value: null, disabled: false }),
        pcs_order_number: new FormControl({ value: null, disabled: false }),
        pcs_effective_month: new FormControl({ value: null, disabled: false }),
        pcs_effective_day: new FormControl({ value: null, disabled: false }),
        pcs_effective_year: new FormControl({ value: null, disabled: false }),

        exception_to_alien_prohibition: new FormControl({
          value: null,
          disabled: false,
        }),

        no_nics_nfa: new FormControl({ value: null, disabled: false }),
        no_nics_valid_permit: new FormControl({
          value: false,
          disabled: false,
        }),
        no_nics_valid_permit_state: new FormControl({
          value: null,
          disabled: false,
        }),
        no_nics_valid_permit_type: new FormControl({
          value: null,
          disabled: false,
        }),
        no_nics_valid_permit_number: new FormControl({
          value: null,
          disabled: false,
        }),
        no_nics_valid_permit_expiration_month: new FormControl({
          value: null,
          disabled: true,
        }),
        no_nics_valid_permit_expiration_day: new FormControl({
          value: null,
          disabled: true,
        }),
        no_nics_valid_permit_expiration_year: new FormControl({
          value: null,
          disabled: true,
        }),
        no_nics_valid_permit_issuance_month: new FormControl({
          value: null,
          disabled: false,
        }),
        no_nics_valid_permit_issuance_day: new FormControl({
          value: null,
          disabled: false,
        }),
        no_nics_valid_permit_issuance_year: new FormControl({
          value: null,
          disabled: false,
        }),

        nics_transmission_month: new FormControl({
          value: null,
          disabled: false,
        }),
        nics_transmission_day: new FormControl({
          value: null,
          disabled: false,
        }),
        nics_transmission_year: new FormControl({
          value: null,
          disabled: false,
        }),
        nics_transaction_number: new FormControl({
          value: null,
          disabled: false,
        }),
        nics_initial_response: new FormControl({
          value: null,
          disabled: false,
        }),

        brady_transfer_month: new FormControl({ value: null, disabled: false }),
        brady_transfer_day: new FormControl({ value: null, disabled: false }),
        brady_transfer_year: new FormControl({ value: null, disabled: false }),
        brady_transfer_number: new FormControl({
          value: null,
          disabled: false,
        }),
        nics_examiner: new FormControl({ value: null, disabled: false }),
        nics_secondary_response_type_prior_to_transfer: new FormControl({
          value: null,
          disabled: false,
        }),
        nics_secondary_response_date_prior_to_transfer: new FormControl({
          value: null,
          disabled: false,
        }),
        nics_secondary_response_type_post_transfer: new FormControl({
          value: null,
          disabled: false,
        }),
        nics_secondary_response_date_post_transfer: new FormControl({
          value: null,
          disabled: false,
        }),
        ffl_employee_completing_nics: new FormControl({
          value: null,
          disabled: false,
        }),
        transferor_signature: new FormControl({
          value: null,
          disabled: true,
        }),
        transferor_name: new FormControl({ value: null, disabled: false }),
        transferor_notes: new FormControl({ value: null, disabled: false }),
        ffl: new FormControl({ value: null, disabled: true }, [
          Validators.required,
        ]),
        ffl_legal_name: new FormControl({ value: null, disabled: true }, [
          Validators.required,
        ]),
        ffl_company_name: new FormControl({ value: null, disabled: true }),
        ffl_address: new FormControl({ value: null, disabled: true }, [
          Validators.required,
        ]),
        ffl_address2: new FormControl({ value: null, disabled: true }),
        ffl_zip_code: new FormControl({ value: null, disabled: true }, [
          Validators.required,
        ]),
        ffl_city: new FormControl({ value: null, disabled: true }, [
          Validators.required,
        ]),
        ffl_state: new FormControl({ value: null, disabled: true }, [
          Validators.required,
        ]),
      },
      { validators: atLeastOneCheckboxValidator },
    );
    this.bsConfig = Object.assign({}, DateMonthPickerConfig);
    this.expBsConfig = Object.assign({}, DateMonthPickerConfig);
    this.expBsConfig.minDate = this.minDate;

    // Subscribe to extensionInstalled$
    this.subscriptions.add(
      this.extensionService.extensionInstalled$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((installed) => {
          this.extensionInstalled = installed;
          //  console.log('[Seller4473Component] Extension installed status:', installed);
        }),
    );

    // Notify user when extension was reloaded and page needs refresh
    this.subscriptions.add(
      this.extensionService.extensionNeedsReload$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.sharedService.raiseWarningToast(
            'The NICS E-Check extension was reloaded. Please refresh this page before sending form data.',
          );
        }),
    );
    // signatureCaptured$
    this.subscriptions.add(
      this.sharedService.signatureCaptured$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((cert_signature) => {
          // this.mapFormToModel(cert_signature);
          // console.log(this.sharedService.form_4473);
          // this.sharedService.changeRoute('seller-form-4473');
        }),
    );
    // drivers_license_id_selected$
    this.subscriptions.add(
      this.sharedService.drivers_license_id_selected$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((drivers_id: string) => {
          const driver_license = findDriverLicense(
            drivers_id,
            this.sharedService,
          );
          const state = this.states.filter(
            (state) => state.state_code === driver_license.addressState,
          )[0].state_name;
          const date = new Date(driver_license.expirationDate);
          this.seller_4473_form.patchValue({
            id_issuing_authority: state,
            id_type_of_id: 'Drivers License',
            id_number: driver_license.driversId,
            id_expiration_month: getMonth(date),
            id_expiration_day: getDay(date),
            id_expiration_year: getYear(date),
            supplemental_id: 'N/A',
          });
        }),
    );
    this.subscriptions.add(
      this.sharedService.drivers_license_scanned$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((driver_license: DriverLicense) => {
          const state = this.states.filter(
            (state) => state.state_code === driver_license.addressState,
          )[0].state_name;
          const date = new Date(driver_license.expirationDate);
          this.seller_4473_form.patchValue({
            id_issuing_authority: state,
            id_type_of_id: 'Drivers License',
            id_number: driver_license.driversId,
            id_expiration_month: getMonth(date),
            id_expiration_day: getDay(date),
            id_expiration_year: getYear(date),
            supplemental_id: 'N/A',
          });
        }),
    );
  }
  ngOnInit(): void {}
  ngAfterViewInit(): void {
    this.sharedService.openStatusSpinner('Auto filling form');
    console.log(
      '[Seller4473Component] ngOnInit: Initializing component for URL:',
      window.location.href,
    );

    // Add delay to allow DOM to fully render
    setTimeout(() => {
      if (this.sharedService.pending_4473._id) {
        this.patchPending4473();
        if (this.seller_4473_form.get('transferor_name').value === '') {
          this.seller_4473_form.patchValue({
            transferor_name:
              this.sharedService.logged_in_user.name_first +
              ' ' +
              this.sharedService.logged_in_user.name_last,
          });
        }
        if (this.seller_4473_form.get('ffl_legal_name').value === '') {
          this.patchFFL();
        }

        if (
          !!findDriversLicenseByUserInfo(
            this.sharedService,
            this.sharedService.pending_4473.transferee,
          )
        ) {
          const driver_license = findDriversLicenseByUserInfo(
            this.sharedService,
            this.sharedService.pending_4473.transferee,
          );
          this.patchDriversLicense(driver_license);
        }
        this.sharedService.closeStatusSpinner();
      } else {
        // Send checkExtension early
        console.log('[Seller4473Component] Sending checkExtension request');
        window.postMessage({ checkExtension: true }, window.location.origin);
        // Delay patch* methods
        setTimeout(() => {
          this.patchFFL();
          this.patchBuyerId();
          this.patchFirearmTypes();
          this.patchTransferor();
          if (!this.sharedService.dispo_build_modal_shown) {
            this.seller_4473_form.get('transferor_signature').enable();
          }
          this.sharedService.closeStatusSpinner();
        }, 1000);
      }
    }, 0); // Small delay to let Angular render first
  }
  showDatepicker(field: string) {
    console.warn(field);
    if (field === 'id_expiration_month' && this.idExpirationMonth) {
      this.idExpirationMonth.show();
    } else if (field === 'pcs_expiration_month' && this.pcsExpirationMonth) {
      this.pcsExpirationMonth.show();
    }
  }
  patchSelectedId(selected_license) {}
  patchFFL() {
    this.seller_4473_form.patchValue({
      transferor_name:
        this.sharedService.logged_in_user.name_first +
        ' ' +
        this.sharedService.logged_in_user.name_last,
      ffl: this.sharedService.company_info.ffl,
      ffl_legal_name: this.sharedService.company_info.legal_name,
      ffl_company_name: this.sharedService.company_info.company_name,
      ffl_address: this.sharedService.company_info.address,
      ffl_address2: this.sharedService.company_info.address2,
      ffl_zip_code: this.sharedService.company_info.zip_code,
      ffl_city: this.sharedService.company_info.city,
      ffl_state: this.sharedService.company_info.state,
    });
  }
  patchBuyerId() {
    if (this.sharedService.dispo_build_modal_shown) {
      if (this.sharedService.dispo_build_selected_drivers_license?.nameFirst) {
        let expiration_date = new Date(
          this.sharedService.dispo_build_selected_drivers_license
            .expirationDate,
        );
        expiration_date.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison
        const expiration_date_month = getMonth(expiration_date);
        const expiration_date_day = getDay(expiration_date);
        const expiration_date_year = getYear(expiration_date);
        this.seller_4473_form.patchValue({
          id_issuing_authority: findStateByAbbreviation(
            this.sharedService.dispo_build_selected_drivers_license
              .addressState,
          ),
          id_type_of_id: 'Drivers License',
          id_number:
            this.sharedService.dispo_build_selected_drivers_license
              .licenseNumber,
          id_expiration_month: expiration_date_month,
          id_expiration_day: expiration_date_day,
          id_expiration_year: expiration_date_year,
        });
      } else if (this.sharedService.dispo_build_manual_entered_id?.id_type) {
        let expiration_date = new Date(
          this.sharedService.dispo_build_manual_entered_id.expiration_date,
        );
        expiration_date.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison
        const expiration_date_month = getMonth(expiration_date);
        const expiration_date_day = getDay(expiration_date);
        const expiration_date_year = getYear(expiration_date);
        console.warn(this.sharedService.dispo_build_manual_entered_id.id_type);
        this.seller_4473_form.patchValue({
          id_issuing_authority:
            this.sharedService.dispo_build_manual_entered_id.issuing_authority,
          id_type_of_id: getIdTypeLabel(
            this.sharedService.dispo_build_manual_entered_id.id_type.toLowerCase(),
          ),
          id_number: this.sharedService.dispo_build_manual_entered_id.id_number,
          id_expiration_month: expiration_date_month,
          id_expiration_day: expiration_date_day,
          id_expiration_year: expiration_date_year,
        });
      }
    } else {
      if (this.sharedService.parsed_license.nameFirst) {
        this.seller_4473_form.patchValue({
          id_issuing_authority: findStateByAbbreviation(
            this.sharedService.parsed_license.addressState,
          ),
          id_type_of_id: 'Drivers License',
          id_number: this.sharedService.parsed_license.licenseNumber,
          id_expiration_month: datePartitioner(
            this.sharedService.parsed_license.expirationDate,
            'm',
          ),
          id_expiration_day: datePartitioner(
            this.sharedService.parsed_license.expirationDate,
            'd',
          ),
          id_expiration_year: datePartitioner(
            this.sharedService.parsed_license.expirationDate,
            'y',
          ),
        });
      }
    }
  }
  patchFirearmTypes() {
    const boundBook = this.sharedService.bound_book;
    if (this.sharedService.dispo_build_reserved_firearms.length > 0) {
      let handgun = false;
      let long_gun = false;
      let other_firearm = false;

      const reserved = this.sharedService.dispo_build_reserved_firearms;
      reserved.forEach((reservedItem) => {
        const match = boundBook.find(
          (bookItem) => bookItem.firearm_id === reservedItem.firearm_id,
        ) as BoundBookEntry;
        if (match) {
          if (
            match.a_firearm_type === 'PISTOL' ||
            match.a_firearm_type === 'REVOLVER'
          ) {
            handgun = true;
          } else if (
            match.a_firearm_type === 'RIFLE' ||
            match.a_firearm_type === 'SHOTGUN'
          ) {
            long_gun = true;
          } else if (
            match.a_firearm_type === 'RECEIVER' ||
            match.a_firearm_type === 'OTHER'
          ) {
            other_firearm = true;
          }
        }
      });
      if (handgun) {
        this.seller_4473_form.patchValue({
          transfer_handgun: true,
        });
      }
      if (long_gun) {
        this.seller_4473_form.patchValue({
          transfer_long_gun: true,
        });
      }
      if (other_firearm) {
        this.seller_4473_form.patchValue({
          transfer_other_firearm: true,
        });
      }
    } else if (this.sharedService.pending_4473._id) {
      let handgun = false;
      let long_gun = false;
      let other_firearm = false;
      const firearms = this.sharedService.pending_4473.firearms;
      firearms.forEach((firearm) => {
        const match = boundBook.find(
          (bookItem) => bookItem.firearm_id === firearm.firearm_id,
        ) as BoundBookEntry;
        if (match) {
          if (
            match.a_firearm_type === 'PISTOL' ||
            match.a_firearm_type === 'REVOLVER'
          ) {
            handgun = true;
          } else if (
            match.a_firearm_type === 'RIFLE' ||
            match.a_firearm_type === 'SHOTGUN'
          ) {
            long_gun = true;
          } else if (
            match.a_firearm_type === 'RECEIVER' ||
            match.a_firearm_type === 'OTHER'
          ) {
            other_firearm = true;
          }
        }
      });
      if (handgun) {
        this.seller_4473_form.patchValue({
          transfer_handgun: true,
        });
      }
      if (long_gun) {
        this.seller_4473_form.patchValue({
          transfer_long_gun: true,
        });
      }
      if (other_firearm) {
        this.seller_4473_form.patchValue({
          transfer_other_firearm: true,
        });
      }
    }
  }
  patchTransferor() {
    this.seller_4473_form.patchValue({
      transferor_name:
        this.sharedService.logged_in_user.name_first +
        ' ' +
        this.sharedService.logged_in_user.name_last,
    });
  }
  patchPending4473() {
    const transferorData = this.sharedService.pending_4473.transferor;
    const firearms = this.sharedService.pending_4473.firearms;
    const boundBook = this.sharedService.bound_book;

    if (!transferorData) {
      console.warn('No transferor data found in pending_4473');
      return;
    }
    // Derive firearm-type booleans from bound book if not explicitly set
    const hasHandgun =
      Array.isArray(firearms) &&
      firearms.some((f) => {
        const bb = boundBook.find((b) => b.firearm_id === f.firearm_id);
        return (
          bb &&
          (bb.a_firearm_type === 'PISTOL' || bb.a_firearm_type === 'REVOLVER')
        );
      });

    const hasLongGun =
      Array.isArray(firearms) &&
      firearms.some((f) => {
        const bb = boundBook.find((b) => b.firearm_id === f.firearm_id);
        return (
          bb &&
          (bb.a_firearm_type === 'RIFLE' || bb.a_firearm_type === 'SHOTGUN')
        );
      });

    const hasOtherFirearm =
      Array.isArray(firearms) &&
      firearms.some((f) => {
        const bb = boundBook.find((b) => b.firearm_id === f.firearm_id);
        return (
          bb &&
          (bb.a_firearm_type === 'RECEIVER' || bb.a_firearm_type === 'OTHER')
        );
      });

    this.seller_4473_form.patchValue({
      // Firearm transfer types
      transfer_handgun: !!transferorData.transfer_handgun || !!hasHandgun,
      transfer_long_gun: !!transferorData.transfer_long_gun || !!hasLongGun,
      transfer_other_firearm:
        !!transferorData.transfer_other_firearm || !!hasOtherFirearm,

      // Function details
      function_name: transferorData.function_name ?? null,
      function_address: transferorData.function_address ?? null,
      function_zip_code: transferorData.function_zip_code ?? null,
      function_city: transferorData.function_city ?? null,
      function_state: transferorData.function_state ?? null,
      function_county: transferorData.function_county ?? null,

      // Identification
      id_issuing_authority: transferorData.id_issuing_authority ?? '',
      id_type_of_id: transferorData.id_type_of_id ?? '',
      id_number: transferorData.id_number ?? '',
      id_expiration_month: transferorData.id_expiration_month ?? '',
      id_expiration_day: transferorData.id_expiration_day ?? '',
      id_expiration_year: transferorData.id_expiration_year ?? '',
      supplemental_id: transferorData.supplemental_id ?? '',

      // PCS (military)
      pcs_base_name: transferorData.pcs_base_name ?? null,
      pcs_order_number: transferorData.pcs_order_number ?? null,
      pcs_effective_month: transferorData.pcs_effective_month ?? null,
      pcs_effective_day: transferorData.pcs_effective_day ?? null,
      pcs_effective_year: transferorData.pcs_effective_year ?? null,

      // Alien exception
      exception_to_alien_prohibition:
        transferorData.exception_to_alien_prohibition ?? null,

      // NICS exemptions
      no_nics_nfa: transferorData.no_nics_nfa ?? null,
      no_nics_valid_permit: transferorData.no_nics_valid_permit ?? false,
      no_nics_valid_permit_state:
        transferorData.no_nics_valid_permit_state ?? null,
      no_nics_valid_permit_type:
        transferorData.no_nics_valid_permit_type ?? null,
      no_nics_valid_permit_number:
        transferorData.no_nics_valid_permit_number ?? null,
      no_nics_valid_permit_expiration_month:
        transferorData.no_nics_valid_permit_expiration_month ?? null,
      no_nics_valid_permit_expiration_day:
        transferorData.no_nics_valid_permit_expiration_day ?? null,
      no_nics_valid_permit_expiration_year:
        transferorData.no_nics_valid_permit_expiration_year ?? null,
      no_nics_valid_permit_issuance_month:
        transferorData.no_nics_valid_permit_issuance_month ?? null,
      no_nics_valid_permit_issuance_day:
        transferorData.no_nics_valid_permit_issuance_day ?? null,
      no_nics_valid_permit_issuance_year:
        transferorData.no_nics_valid_permit_issuance_year ?? null,

      // NICS background check
      nics_transmission_month: transferorData.nics_transmission_month ?? '',
      nics_transmission_day: transferorData.nics_transmission_day ?? '',
      nics_transmission_year: transferorData.nics_transmission_year ?? '',
      nics_transaction_number: transferorData.nics_transaction_number ?? null,
      nics_initial_response: transferorData.nics_initial_response ?? null,

      // Brady transfer
      brady_transfer_month: transferorData.brady_transfer_month ?? null,
      brady_transfer_day: transferorData.brady_transfer_day ?? null,
      brady_transfer_year: transferorData.brady_transfer_year ?? null,
      brady_transfer_number: transferorData.brady_transfer_number ?? null,

      // NICS follow-up
      nics_examiner: transferorData.nics_examiner ?? null,
      nics_secondary_response_type_prior_to_transfer:
        transferorData.nics_secondary_response_type_prior_to_transfer ?? null,
      nics_secondary_response_date_prior_to_transfer:
        transferorData.nics_secondary_response_date_prior_to_transfer ?? null,
      nics_secondary_response_type_post_transfer:
        transferorData.nics_secondary_response_type_post_transfer ?? null,
      nics_secondary_response_date_post_transfer:
        transferorData.nics_secondary_response_date_post_transfer ?? null,

      // FFL employee
      ffl_employee_completing_nics:
        transferorData.ffl_employee_completing_nics ?? null,
      transferor_signature: transferorData.transferor_signature ?? null,
      transferor_name: transferorData.transferor_name ?? '',
      transferor_notes: transferorData.transferor_notes ?? null,

      // FFL dealer info
      ffl: transferorData.ffl ?? '',
      ffl_legal_name: transferorData.ffl_legal_name ?? '',
      ffl_company_name: transferorData.ffl_company_name ?? '',
      ffl_address: transferorData.ffl_address ?? '',
      ffl_address2: transferorData.ffl_address2 ?? '',
      ffl_zip_code: transferorData.ffl_zip_code ?? '',
      ffl_city: transferorData.ffl_city ?? '',
      ffl_state: transferorData.ffl_state ?? '',
    });

    // Close the status modal after patching
    this.sharedService.closeStatusSpinner();
  }
  patchDriversLicense(drivers_license: DriverLicense) {
    const expiration_date = new Date(drivers_license.expirationDate);
    this.seller_4473_form.patchValue({
      id_issuing_authority: getStateNameByStateCode(
        drivers_license.addressState,
      ),
      id_type_of_id: 'Driver License',
      id_number: drivers_license.licenseNumber,
      id_expiration_month: getMonth(expiration_date),
      id_expiration_day: getDay(expiration_date),
      id_expiration_year: getYear(expiration_date),
    });
  }
  sendFormData() {
    const dispoBuildTransferee = this.sharedService.dispo_build_selected_transferee_4473;
    const transferee =
        (dispoBuildTransferee?.name_first ? dispoBuildTransferee : null)
        ?? this.sharedService.pending_4473?.transferee;
      console.log('[sendFormData] transferee:', transferee);
      const formData: SendFormDataToExtensionMessage = {
        title: `${transferee?.name_first} ${transferee?.name_last}`,
        backgroundCheckUrl: 'https://www.cjis.gov/nics/PRServletSSO',

        pages: [
          {
            // Subject Data Page
            title: 'Subject Data',
            activeTabUrlRegex: '^https://www.cjis.gov/nics/PRServletSSO.*',
            fillDocumentUrlRegex: '^https://www\\.cjis\\.gov/nics/PRServletSSO.*',
            allFrames: true,
            alert: {
              before: 'Filling Subject Data...',
              after: 'Subject Data Filled!',
            },
            number: 1,
            loggedInUserEmail: 'noemail@gmail.com', // Update to real email
            formUrl: window.location.href,
            errorReportUrl: 'https://your-app.com/support',
            condition: 'input[title="The last name of a subject"]', // Unique title as condition
            fields: [
              {
                method: 'querySelector',
                id: 'input[title="The last name of a subject"]',
                value: transferee.name_last || '',
              },
              {
                method: 'querySelector',
                id: 'input[title="The subject\'s first name"]',
                value: transferee.name_first || '',
              },
              ...(transferee.name_middle ? [{
                method: 'querySelector' as const,
                id: 'input[title="The subject\'s middle name or middle initial"]',
                value: transferee.name_middle,
              }] : []),
              {
                method: 'querySelector',
                id: 'select[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pCADENCE_CODE"]',
                value: transferee.cadence || '',
              },
              {
                method: 'querySelector',
                id: 'input[type="checkbox"][name*="IOChecked"]',
                checked: transferee.name_middle?.trim().length === 1 || transferee.name_middle?.includes('IO') || false,
              },
              {
                method: 'querySelector',
                id: 'input[type="checkbox"][name*="NMNChecked"]',
                checked: !transferee.name_middle || transferee.name_middle.includes('NMN'),
              },
              {
                method: 'querySelector',
                id: 'input[title="State of Residence"]',
                value: StateList.find(s => s.state_name === (transferee.address_state || '').toUpperCase())?.state_code || transferee.address_state || '',
              },
              {
                method: 'querySelector',
                id: 'input[title="Place of Birth"]',
                value: StateList.find(s => s.state_name === (transferee.birth_state || '').toUpperCase())?.state_code || transferee.birth_state || '',
              },
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pSearchRequest$pHeightString"]',
                value: transferee.height_feet != null && transferee.height_inches != null
                  ? `${transferee.height_feet}${String(transferee.height_inches).padStart(2, '0')}`
                  : '',
              },
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pSearchRequest$pWeightString"]',
                value: transferee.weight?.toString() || '',
              },
              {
                method: 'querySelector',
                id: 'select[title="Gender Drop Down (Ex: Female, Male, Non-Binary)"]',
                text:
                  transferee.sex === 'Male'
                    ? 'Male'
                    : transferee.sex === 'Female'
                      ? 'Female'
                      : 'Unknown',
                fireEvents: ['change'],
              },
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pDOB"]',
                value: `${transferee?.birth_month}/${transferee?.birth_day}/${transferee?.birth_year}`,
              },
              {
                method: 'querySelector',
                id: 'input[title="The Social Security Number of a subject"]',
                value: transferee.social_security?.replace(/-/g, '') || '',
              },
              {
                method: 'querySelector',
                id: 'input[title="A unique number assigned to every individual whose record(s) is entered into the VAF"]',
                value: transferee.upin || '',
              },
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pSearchRequest$pPurge$pFFLVerification"][value="Yes"]',
                checked: true,
              },
            ],
          },
          {
            // Race and Ethnicity Page
            title: 'Race and Ethnicity',
            activeTabUrlRegex: '^https://www.cjis.gov/nics/PRServletSSO.*',
            fillDocumentUrlRegex: '^https://www.cjis.gov/nics/PRServletSSO.*',
            allFrames: true,
            alert: {
              before: 'Filling Race and Ethnicity Details...',
              after: 'Race and Ethnicity filled!',
            },
            number: 2,
            loggedInUserEmail: 'noemail@gmail.com',
            formUrl: window.location.href,
            errorReportUrl: 'https://your-app.com/support',
            condition:
              'input[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pisHispanicOrLatino"][value="true"]', // Unique ethnicity radio as condition
            fields: [
              ...(transferee.ethnicity?.toUpperCase() === 'IS HISPANIC OR LATINO' || transferee.ethnicity?.toUpperCase() === 'HISPANIC OR LATINO' ? [{
                method: 'querySelector' as const,
                id: 'input[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pisHispanicOrLatino"][value="true"]',
                checked: true,
              }] : []),
              ...(transferee.ethnicity?.toUpperCase() === 'IS NOT HISPANIC OR LATINO' || transferee.ethnicity?.toUpperCase() === 'NOT HISPANIC OR LATINO' ? [{
                method: 'querySelector' as const,
                id: 'input[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pisHispanicOrLatino"][value="false"]',
                checked: true,
              }] : []),
              ...(transferee.race?.some((race) => race.toLowerCase() === 'asian') ? [{
                method: 'querySelector' as const,
                id: 'input[type="checkbox"][name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pRace$l1$pisChecked"]',
                checked: true,
              }] : []),
              ...(transferee.race?.some((race) => race.toLowerCase() === 'black or african american') ? [{
                method: 'querySelector' as const,
                id: 'input[type="checkbox"][name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pRace$l2$pisChecked"]',
                checked: true,
              }] : []),
              ...(transferee.race?.some((race) => race.toLowerCase() === 'american indian or alaskan native') ? [{
                method: 'querySelector' as const,
                id: 'input[type="checkbox"][name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pRace$l4$pisChecked"]',
                checked: true,
              }] : []),
              ...(transferee.race?.some((race) => race.toLowerCase() === 'middle eastern/north african') ? [{
                method: 'querySelector' as const,
                id: 'input[type="checkbox"][name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pRace$l5$pisChecked"]',
                checked: true,
              }] : []),
              ...(transferee.race?.some((race) => race.toLowerCase() === 'native hawaiian or other pacific islander') ? [{
                method: 'querySelector' as const,
                id: 'input[type="checkbox"][name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pRace$l6$pisChecked"]',
                checked: true,
              }] : []),
              ...(transferee.race?.some((race) => race.toLowerCase() === 'white') ? [{
                method: 'querySelector' as const,
                id: 'input[type="checkbox"][name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pRace$l7$pisChecked"]',
                checked: true,
              }] : []),
              {
                method: 'querySelector',
                id: 'select[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pCITIZEN_STATUS_CODE"]',
                value: transferee.citizenship?.includes('UNITED STATES')
                  ? 'C'
                  : 'F',
              },
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pCITIZEN_COUNTRY_CODE"]',
                value:
                  !transferee.citizenship?.includes('UNITED STATES') &&
                  transferee.citizenship?.[0]
                    ? transferee.citizenship[0]
                    : '',
              },
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pCITIZEN_COUNTRY_CODE_2"]',
                value:
                  !transferee.citizenship?.includes('UNITED STATES') &&
                  transferee.citizenship?.length > 1
                    ? transferee.citizenship[1]
                    : '',
              },
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pSubject$pCITIZEN_COUNTRY_CODE_3"]',
                value:
                  !transferee.citizenship?.includes('UNITED STATES') &&
                  transferee.citizenship?.length > 2
                    ? transferee.citizenship[2]
                    : '',
              },
              {
                method: 'querySelector',
                id: 'select[title="Non-Immigrant with Visa Exception (No, Not Answered, Yes)"]',
                value: '', // Set to 'Y', 'N', or 'X' based on data if available
              },
            ],
          },
          {
            // Additional Info Page
            title: 'Additional Info',
            activeTabUrlRegex: '^https://www.cjis.gov/nics/PRServletSSO.*',
            fillDocumentUrlRegex: '^https://www.cjis.gov/nics/PRServletSSO.*',
            allFrames: true,
            alert: {
              before: 'Filling Additional Info Page...',
              after: 'Additional Info Page filled!',
            },
            number: 3,
            loggedInUserEmail: 'noemail@gmail.com',
            formUrl: window.location.href,
            errorReportUrl: 'https://your-app.com/support',
            condition: 'select[title="Miscellaneous Number Type"]', // Unique select title as condition
            fields: [
              {
                method: 'querySelector',
                id: 'select[title="Miscellaneous Number Type"]',
                value: 'DL', // For Driver's License
              },
              {
                method: 'querySelector',
                id: 'input[title="Miscellaneous Number"]',
                value:
                  this.sharedService.dispo_build_selected_drivers_license
                    ?.driversId || '',
              },
              ...(this.seller_4473_form.get('transfer_handgun')?.value ? [{
                method: 'querySelector' as const,
                id: 'input[type="checkbox"][name="$PpyWorkPage$pSearchRequest$pPurge$pTransPurpose$l1$pisChecked"]',
                checked: true,
              }] : []),
              ...(this.seller_4473_form.get('transfer_long_gun')?.value ? [{
                method: 'querySelector' as const,
                id: 'input[type="checkbox"][name="$PpyWorkPage$pSearchRequest$pPurge$pTransPurpose$l2$pisChecked"]',
                checked: true,
              }] : []),
              ...(this.seller_4473_form.get('transfer_other_firearm')?.value ? [{
                method: 'querySelector' as const,
                id: 'input[type="checkbox"][name="$PpyWorkPage$pSearchRequest$pPurge$pTransPurpose$l3$pisChecked"]',
                checked: true,
              }] : []),
              // Add more for l4 to l11 if needed, e.g.:
              // {
              //   method: 'querySelector',
              //   id: 'input[name="$PpyWorkPage$pSearchRequest$pPurge$pTransPurpose$l4$pisChecked"]',
              //   checked: // logic for pre-pawn hand gun
              // },
              {
                method: 'querySelector',
                id: 'textarea[title="Miscellaneous Information textbox"]',
                value: '', // Add notes if available
              },
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pMAILING_NAME"]',
                value: '', // Contact name
              },
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pNICS_CONTACT_PHONE"]',
                value: '', // Contact phone
              },
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pSearchRequest$pPartialPurge$pCONTACT_HOURS"]',
                value: '', // Callback hours
              },
            ],
          },
          {
            // Verify Search Information Page
            title: 'Verify Search Information',
            activeTabUrlRegex: '^https://www.cjis.gov/nics/PRServletSSO.*',
            fillDocumentUrlRegex: '^https://www.cjis.gov/nics/PRServletSSO.*',
            allFrames: true,
            alert: {
              before: 'Filling Verify Page...',
              after: 'Verify Page filled!',
            },
            number: 4,
            loggedInUserEmail: 'noemail@gmail.com',
            formUrl: window.location.href,
            errorReportUrl: 'https://your-app.com/support',
            condition: 'select[name="$PpyWorkPage$pCADENCE_CODE"]', // Unique to Verify frame (no $pSearchRequest path)
            fields: [
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pLAST_NAME"]',
                value: transferee.name_last || '',
              },
              {
                method: 'querySelector',
                id: 'input[name="$PpyWorkPage$pFIRST_NAME"]',
                value: transferee.name_first || '',
              },
              ...(transferee.name_middle ? [{
                method: 'querySelector' as const,
                id: 'input[title="The subject\'s middle name or initial"]',
                value: transferee.name_middle,
              }] : []),
              {
                method: 'querySelector',
                id: 'select[name="$PpyWorkPage$pCADENCE_CODE"]',
                value: transferee.cadence || '',
              },
              {
                method: 'querySelector',
                id: 'input[type="checkbox"][name*="IOChecked"]',
                checked: transferee.name_middle?.trim().length === 1 || transferee.name_middle?.includes('IO') || false,
              },
              {
                method: 'querySelector',
                id: 'input[type="checkbox"][name*="NMNChecked"]',
                checked: !transferee.name_middle || transferee.name_middle.includes('NMN'),
              },
            ],
          },
        ],
      };

      const message: NicsECheckExtensionMessageEnvelope = {
        extensionType: 'NicsECheck.Autofill',
        extensionVersion: 1,
        messageType: 'SendFormDataToExtension',
        dataJson: JSON.stringify(formData),
      };

      console.log('[seller-4473] Sending to extension:', formData);
      window.postMessage(message, window.location.origin);
      this.sharedService.openExternalLink(
        'https://www.cjis.gov/nics/PRServletSSO',
      );
  }
  openInstructions(question_number: string) {
    if (this.selected_instruction?.question === question_number) {
      this.selected_instruction.title = null;
      this.selected_instruction.body = null;
      this.selected_instruction.question = null;
    } else {
      for (const question of Instructions) {
        if (question.question === question_number) {
          this.selected_instruction.title = question.title;
          this.selected_instruction.body = question.body;
          this.selected_instruction.question = question.question;
        }
      }
    }
  }
  closeInstructions() {
    this.selected_instruction.title = null;
    this.selected_instruction.body = null;
  }
  completeSellerFormClick() {
    // meed to complete
  }
  showFormClick() {
    this.closeComponent();
  }
  openDigitalSignaturePad() {
    this.sharedService.openDigitalSignature(
      signatureType.find((s) => s.name === 'ffl_employee').name,
    );
  }
  patchTodaysDate() {
    this.seller_4473_form.patchValue({
      nics_transmission_month: getMonth(),
      nics_transmission_day: getDay(),
      nics_transmission_year: getYear(),
    });
  }
  openNicsCheck() {
    // this.sharedService.openExternalLink('https://www.cjis.gov/nics/PRServletSSO')
    this.sendFormData();
  }
  closeComponent() {
    // build_dispo
    if (this.sharedService.dispo_build_modal_shown) {
      this.sharedService.closeModal(ModalID.dispo_build);
      setTimeout(() => {
        this.sharedService.dispo_build_modal_shown = false;
      }, 200);
      // pending_4473
    } else if (this.sharedService.pending_4473) {
      this.sharedService.changeRoute('pending-dispositions');
    } else {
      this.sharedService.closeModal(ModalID.drivers_license_selection);
      this.sharedService.changeRoute('home');
    }
  }
  noNicsValidPermitClick() {
    if (this.seller_4473_form.get('no_nics_valid_permit').value) {
      this.seller_4473_form
        .get('no_nics_valid_permit_state')
        .setValidators([Validators.required]);
      this.seller_4473_form
        .get('no_nics_valid_permit_type')
        .setValidators([Validators.required]);
      this.seller_4473_form
        .get('no_nics_valid_permit_number')
        .setValidators([Validators.required]);
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_month')
        .setValidators([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(2),
          Validators.pattern('^(0[1-9]|1[0-2])$'),
          checkForValidExpirationDate('no_nics_valid_permit_expiration'),
        ]);
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_day')
        .setValidators([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(2),
          Validators.pattern('^(0[1-9]|[12][0-9]|3[01])$'),
          checkForValidExpirationDate('no_nics_valid_permit_expiration'),
        ]);
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_year')
        .setValidators([
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(4),
          Validators.pattern('^[0-9]{4}$'),
          checkForValidExpirationDate('no_nics_valid_permit_expiration'),
        ]);
      this.seller_4473_form
        .get('no_nics_valid_permit_issuance_month')
        .setValidators([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(2),
          Validators.pattern('^(0[1-9]|1[0-2])$'),
        ]);
      this.seller_4473_form
        .get('no_nics_valid_permit_issuance_day')
        .setValidators([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(2),
          Validators.pattern('^(0[1-9]|[12][0-9]|3[01])$'),
        ]);
      this.seller_4473_form
        .get('no_nics_valid_permit_issuance_year')
        .setValidators([
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(4),
          Validators.pattern('^[0-9]{4}$'),
        ]);
    } else {
      this.seller_4473_form
        .get('no_nics_valid_permit_state')
        .setValidators(null);
      this.seller_4473_form
        .get('no_nics_valid_permit_type')
        .setValidators(null);
      this.seller_4473_form
        .get('no_nics_valid_permit_number')
        .setValidators(null);
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_month')
        .setValidators(null);
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_day')
        .setValidators(null);
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_year')
        .setValidators(null);
      this.seller_4473_form
        .get('no_nics_valid_permit_issuance_month')
        .setValidators(null);
      this.seller_4473_form
        .get('no_nics_valid_permit_issuance_day')
        .setValidators(null);
      this.seller_4473_form
        .get('no_nics_valid_permit_issuance_year')
        .setValidators(null);
    }
    this.seller_4473_form
      .get('no_nics_valid_permit_expiration_year')
      .updateValueAndValidity();
    this.seller_4473_form
      .get('no_nics_valid_permit_expiration_month')
      .updateValueAndValidity();
    this.seller_4473_form
      .get('no_nics_valid_permit_expiration_day')
      .updateValueAndValidity();
    this.seller_4473_form
      .get('no_nics_valid_permit_issuance_year')
      .updateValueAndValidity();
    this.seller_4473_form
      .get('no_nics_valid_permit_issuance_month')
      .updateValueAndValidity();
    this.seller_4473_form
      .get('no_nics_valid_permit_issuance_day')
      .updateValueAndValidity();
    this.seller_4473_form
      .get('no_nics_valid_permit_state')
      .updateValueAndValidity();
    this.seller_4473_form
      .get('no_nics_valid_permit_type')
      .updateValueAndValidity();
    this.seller_4473_form
      .get('no_nics_valid_permit_number')
      .updateValueAndValidity();
    this.shouldDisableDatepicker();
  }
  dateChanged(date: Date, input: string): void {
    if (!date) {
      return;
    }
    const month = getMonth(date);
    const day = getDay(date);
    const year = getYear(date);
    if (date && input === 'nics_transmission_date') {
      this.seller_4473_form.patchValue({
        nics_transmission_month: month,
        nics_transmission_day: day,
        nics_transmission_year: year,
      });
      this.setOptionalDateValidators('nics_transmission');
    } else if (date && input === 'no_nics_valid_permit_issuance_date') {
      console.log(input);
      this.seller_4473_form.patchValue({
        no_nics_valid_permit_issuance_month: month,
        no_nics_valid_permit_issuance_day: day,
        no_nics_valid_permit_issuance_year: year,
      });
    } else if (date && input === 'no_nics_valid_permit_expiration_date') {
      this.seller_4473_form.patchValue({
        no_nics_valid_permit_expiration_month: month,
        no_nics_valid_permit_expiration_day: day,
        no_nics_valid_permit_expiration_year: year,
      });
      // check to see if the expiration date is after the issuance date
      if (!this.checkForValidExpirationDate(day, month, year)) {
        this.sharedService.raiseWarningToast(
          'The expiration date entered is not valid. Please enter a date that is after today.',
        );
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_month')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_day')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_year')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_year')
          .updateValueAndValidity();
      } else {
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_year')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_month')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_day')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_year')
          .updateValueAndValidity();
      }
      if (
        !this.checkIssueVsExpirationDate('no_nics_valid_permit_expiration_date')
      ) {
        this.sharedService.raiseWarningToast(
          'The issuance date must be before the expiration date.',
        );
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_month')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_day')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_year')
          .setErrors({ custom: true });

        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_month')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_day')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_year')
          .setErrors({ custom: true });
      } else {
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_year')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_month')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_day')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_year')
          .updateValueAndValidity();

        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_year')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_month')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_day')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_year')
          .updateValueAndValidity();
      }
      //check if the expiration date is still valid
    } else if (date && input === 'id_expiration_date') {
      this.seller_4473_form.patchValue({
        id_expiration_month: month,
        id_expiration_day: day,
        id_expiration_year: year,
      });

      // check to see if the expiration date is after the issuance date
      if (!this.checkForValidExpirationDate(day, month, year)) {
        this.sharedService.raiseWarningToast(
          'The expiration date entered is not valid. Please enter a date that is after today.',
        );
        this.seller_4473_form
          .get('id_expiration_month')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('id_expiration_day')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('id_expiration_year')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('id_expiration_year')
          .updateValueAndValidity();
      } else {
        this.seller_4473_form.get('id_expiration_year').setErrors(null);
        this.seller_4473_form.get('id_expiration_month').setErrors(null);
        this.seller_4473_form.get('id_expiration_day').setErrors(null);
        this.seller_4473_form
          .get('id_expiration_year')
          .updateValueAndValidity();
      }
    } else if (date && input === 'pcs_effective_date') {
      this.seller_4473_form.patchValue({
        pcs_effective_month: month,
        pcs_effective_day: day,
        pcs_effective_year: year,
      });
      this.setOptionalDateValidators('pcs_effective');
    }
  }
  verifyDate(input: string) {
    if (input === 'no_nics_valid_permit_issuance_date') {
      const issue_day = this.seller_4473_form.get(
        'no_nics_valid_permit_issuance_day',
      ).value;
      let issue_month = this.seller_4473_form.get(
        'no_nics_valid_permit_issuance_month',
      ).value;
      issue_month = getMonth(issue_month);
      const issue_year = this.seller_4473_form.get(
        'no_nics_valid_permit_issuance_year',
      ).value;

      const expiration_day = this.seller_4473_form.get(
        'no_nics_valid_permit_expiration_day',
      ).value;
      let expiration_month = this.seller_4473_form.get(
        'no_nics_valid_permit_expiration_month',
      ).value;
      expiration_month = getMonth(expiration_month);
      const expiration_year = this.seller_4473_form.get(
        'no_nics_valid_permit_expiration_year',
      ).value;

      if (
        !this.checkForValidExpirationDate(issue_day, issue_month, issue_year)
      ) {
        this.invalidExpirationDateWarning();
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_month')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_day')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_year')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_year')
          .updateValueAndValidity();
      } else {
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_year')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_month')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_day')
          .setErrors(null);
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_year')
          .updateValueAndValidity();
      }
      if (
        !this.checkIssueVsExpirationDate('no_nics_valid_permit_issuance_date')
      ) {
        this.sharedService.raiseWarningToast(
          'The issuance date must be before the expiration date.',
        );
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_month')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_day')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_expiration_year')
          .setErrors({ custom: true });

        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_month')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_day')
          .setErrors({ custom: true });
        this.seller_4473_form
          .get('no_nics_valid_permit_issuance_year')
          .setErrors({ custom: true });
      }
    } else if (input === 'id_expiration_date') {
      const expiration_day =
        this.seller_4473_form.get('id_expiration_day').value;
      let expiration_month = this.seller_4473_form.get(
        'id_expiration_month',
      ).value;
      expiration_month = getMonth(expiration_month);
      const expiration_year =
        this.seller_4473_form.get('id_expiration_year').value;
      if (
        !this.checkForValidExpirationDate(
          expiration_day,
          expiration_month,
          expiration_year,
        )
      ) {
        this.invalidExpirationDateWarning();
        this.setDateErrors('id_expiration', true);
        return;
      }
    }
  }

  invalidExpirationDateWarning() {
    this.sharedService.raiseWarningToast(
      'The expiration date entered is not valid. Please enter a date that is after today.',
    );
  }
  checkForValidExpirationDate(
    day: string,
    month: string,
    year: string,
  ): boolean {
    let today = new Date();
    today.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison
    let expirationDate = new Date(
      `${Number(year)}-${Number(month)}-${Number(day)}`,
    );
    expirationDate.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison
    return expirationDate >= today;
  }
  checkForValidIssueDate(day: string, month: string, year: string): boolean {
    let today = new Date();
    today.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison
    let issueDate = new Date(`${Number(year)}-${Number(month)}-${Number(day)}`);
    issueDate.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison
    return issueDate <= today;
  }
  checkIssueVsExpirationDate(input: string): boolean {
    const issue_date = new Date(
      this.seller_4473_form.get(`no_nics_valid_permit_issuance_month`).value,
    );
    issue_date.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison
    const issueMonth = getMonth(issue_date);
    const issueDay = this.seller_4473_form.get(
      `no_nics_valid_permit_issuance_day`,
    ).value;
    const issueYear = this.seller_4473_form.get(
      `no_nics_valid_permit_issuance_year`,
    ).value;

    const expiration_date = new Date(
      this.seller_4473_form.get(`no_nics_valid_permit_expiration_month`).value,
    );
    expiration_date.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison
    const expirationMonth = getMonth(expiration_date);
    const expirationDay = this.seller_4473_form.get(
      `no_nics_valid_permit_expiration_day`,
    ).value;
    const expirationYear = this.seller_4473_form.get(
      `no_nics_valid_permit_expiration_year`,
    ).value;

    if (
      issueMonth &&
      issueDay &&
      issueYear &&
      expirationMonth &&
      expirationDay &&
      expirationYear
    ) {
      let issueDate = new Date(
        `${Number(issueYear)}-${Number(issueMonth)}-${Number(issueDay)}`,
      );
      issueDate.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison
      let expirationDate = new Date(
        `${Number(expirationYear)}-${Number(expirationMonth)}-${Number(
          expirationDay,
        )}`,
      );
      expirationDate.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison
      return issueDate <= expirationDate;
    } else {
      console.warn('here');
      return false;
    }
  }
  private setDateErrors(prefix: string, hasError: boolean): void {
    const fields = ['month', 'day', 'year'];
    fields.forEach((field) => {
      const control = this.seller_4473_form.get(`${prefix}_${field}`);
      console.error(hasError);
      if (hasError) {
        control.setErrors({ custom: true });
        control.markAsDirty();
        control.markAsTouched();
        console.log(
          `${prefix}_${field} errors:`,
          control.errors,
          `dirty:`,
          control.dirty,
        );
      } else {
        control.setErrors(null);
      }
      control.updateValueAndValidity({ emitEvent: true });
    });
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  onInputClick(event: Event, picker: BsDatepickerDirective): void {
    event.stopPropagation();
    event.preventDefault();
    // Hide all date pickers
    if (picker) {
      picker.hide();
    }
  }
  openDatepickerAtButton(
    event: MouseEvent,
    datepicker: BsDatepickerDirective,
    inputRef: ElementRef,
  ) {
    const button = event.target as HTMLElement;
    const rect = button.getBoundingClientRect();

    // Move the hidden input to the button's position
    const inputEl = inputRef.nativeElement as HTMLElement;
    inputEl.style.left = `${rect.left + window.scrollX}px`;
    inputEl.style.top = `${rect.top + window.scrollY}px`;

    // Show the datepicker
    datepicker.show();
  }
  setOptionalDateValidators(prefix: string) {
    let control_group = [];
    const control_month = this.seller_4473_form.get(`${prefix}_month`);
    const control_day = this.seller_4473_form.get(`${prefix}_day`);
    const control_year = this.seller_4473_form.get(`${prefix}_year`);
    control_group.push(control_month, control_day, control_year);

    if (!control_month && !control_day && !control_year) return;
    if (control_month.value || control_day.value || control_year.value) {
      for (const control of control_group) {
        const controlName = Object.keys(this.seller_4473_form.controls).find(
          (name) => this.seller_4473_form.get(name) === control,
        );
        if (!control.validator) {
          if (controlName.includes('year')) {
            control.setValidators([
              Validators.required,
              Validators.minLength(4),
              Validators.maxLength(4),
              checkForValidExpirationDate(prefix),
            ]);
          } else {
            control.setValidators([
              Validators.required,
              Validators.minLength(2),
              Validators.maxLength(2),
              checkForValidExpirationDate(prefix),
            ]);
          }
        }
      }
    } else {
      for (const control of control_group) {
        console.log('here');
        control.clearValidators();
      }
    }
    for (const control of control_group) {
      control.updateValueAndValidity();
    }
  }
  shouldDisableDatepicker(): void {
    if (
      this.seller_4473_form.get('no_nics_valid_permit_issuance_month')
        .invalid ||
      this.seller_4473_form.get('no_nics_valid_permit_issuance_day').invalid ||
      this.seller_4473_form.get('no_nics_valid_permit_issuance_year').invalid
    ) {
      this.disable_datepicker = true;
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_month')
        .disable();
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_day')
        .disable();
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_year')
        .disable();
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_month')
        .patchValue(null);
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_day')
        .patchValue(null);
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_year')
        .patchValue(null);
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_month')
        .updateValueAndValidity();
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_day')
        .updateValueAndValidity();
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_year')
        .updateValueAndValidity();
    } else {
      this.disable_datepicker = false;
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_month')
        .enable();
      this.seller_4473_form.get('no_nics_valid_permit_expiration_day').enable();
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_year')
        .enable();
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_month')
        .updateValueAndValidity();
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_day')
        .updateValueAndValidity();
      this.seller_4473_form
        .get('no_nics_valid_permit_expiration_year')
        .updateValueAndValidity();
    }
  }
  functionValidation() {
    if (this.seller_4473_form.get('function_name').value) {
      this.seller_4473_form
        .get('function_address')
        .setValidators([Validators.required]);
      this.seller_4473_form
        .get('function_city')
        .setValidators([Validators.required]);
      this.seller_4473_form
        .get('function_state')
        .setValidators([Validators.required]);
      this.seller_4473_form
        .get('function_zip_code')
        .setValidators([Validators.required]);
    } else {
      this.seller_4473_form.get('function_address').clearValidators();
      this.seller_4473_form.get('function_city').clearValidators();
      this.seller_4473_form.get('function_state').clearValidators();
      this.seller_4473_form.get('function_zip_code').clearValidators();
    }
  }
  pcsValidation() {
    if (this.seller_4473_form.get('pcs_base_name').value) {
      this.seller_4473_form
        .get('pcs_effective_month')
        .setValidators([Validators.required]);
      this.seller_4473_form
        .get('pcs_effective_day')
        .setValidators([Validators.required]);
      this.seller_4473_form
        .get('pcs_effective_year')
        .setValidators([Validators.required]);
    } else {
      this.seller_4473_form.get('pcs_effective_month').clearValidators();
      this.seller_4473_form.get('pcs_effective_day').clearValidators();
      this.seller_4473_form.get('pcs_effective_year').clearValidators();
    }
  }
  nicsValidation() {
    if (this.seller_4473_form.get('nics_transmission_month').value) {
      this.seller_4473_form
        .get('nics_transmission_number')
        .setValidators([
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(8),
        ]);
      this.seller_4473_form
        .get('nics_initial_response')
        .setValidators([Validators.required]);
    } else {
      this.seller_4473_form.get('nics_transmission_number').clearValidators();
      this.seller_4473_form.get('nics_initial_response').clearValidators();
    }
  }
  sign4473() {
    const digital_signature = this.sharedService.company_info.users.filter(
      (user) => user.user_id === this.sharedService.logged_in_user.user_id,
    )[0].signature;
    if (digital_signature) {
      const full_name =
        this.sharedService.logged_in_user.name_first +
        ' ' +
        this.sharedService.logged_in_user.name_last;
      this.seller_4473_form.get('transferor_signature').patchValue(full_name);
    } else {
      this.sharedService.openDigitalSignature(
        signatureType.find((s) => s.name === 'ffl_employee').name,
      );
    }
  }
  async submitFormClick() {
    if (this.sharedService.pending_4473._id) {
      const seller_4473 = this.seller_4473_form.getRawValue();
      this.sharedService.pending_4473.transferor = seller_4473;
      await this.sharedService.updateForm4473(this.sharedService.pending_4473);
      this.sharedService.generateAndDisplay4473(
        this.sharedService.pending_4473,
      );
    } else {
      console.warn(this.seller_4473_form.getRawValue());
      this.sharedService.dispo_build_seller_4473 =
        this.seller_4473_form.getRawValue();
    }
  }
  async saveFormClick() {
    if (this.sharedService.pending_4473._id) {
      const seller_4473 = this.seller_4473_form.getRawValue();
      console.warn(seller_4473);
      this.sharedService.pending_4473.transferor = seller_4473;
      await this.sharedService.updateForm4473(this.sharedService.pending_4473);
      this.sharedService.changeRoute('pending-dispositions');
    } else {
      this.sharedService.dispo_build_seller_4473 =
        this.seller_4473_form.getRawValue();
      this.sharedService.seller_form_4473_saved$.next(true);
      this.sharedService.closeModal(ModalID.dispo_build);
    }
  }
  clearFormClick() {
    this.seller_4473_form.reset();
  }

  get buyerName(): string {
    const formatInfo = (transferee: any) => {
      let info = '';
      if (transferee.name_first && transferee.name_last) {
        info += `${transferee.name_first} ${transferee.name_last}`;
      }
      if (transferee.birth_month && transferee.birth_day && transferee.birth_year) {
        info += ` (${transferee.birth_month}/${transferee.birth_day}/${transferee.birth_year})`;
      }
      return info;
    };

    // Check pending disposition first
    if (this.sharedService.pending_4473?._id && this.sharedService.pending_4473?.transferee) {
      const info = formatInfo(this.sharedService.pending_4473.transferee);
      if (info) return info;
    }
    // Fallback to building disposition
    if (this.sharedService.dispo_build_selected_transferee_4473) {
      const info = formatInfo(this.sharedService.dispo_build_selected_transferee_4473);
      if (info) return info;
    }
    return '';
  }
}
// custom validators
export function atLeastOneCheckboxValidator(): ValidatorFn {
  return (formGroup: FormGroup): ValidationErrors | null => {
    const handgun = formGroup.get('transfer_handgun').value;
    const longGun = formGroup.get('transfer_long_gun').value;
    const otherFirearm = formGroup.get('transfer_other_firearm').value;

    // Return null if at least one checkbox is checked (valid), otherwise return an error
    return handgun || longGun || otherFirearm
      ? null
      : { atLeastOneRequired: true };
  };
}

// need to make it so if the mics check is done or there is a CWP and the buyer certification is today and the form has been reviewed then user can sign and complete the dispo
// need to make it so after a 4473 is completed the buyers Drivers license data if any has been deleted
// Also need to encrpt the drivers license data as well as form data
// create an aduit tool when selecting a drivers license that notify the seller if any of the buyers fields are differnt then the drivers license data on file and allow the seller to view the buyers drivers license image on file as well as the entered data side by side to compare before selecting the drivers license to pull data from for the 4473 form